import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-2" };
const _hoisted_2 = { class: "text-sm font-medium text-(--colors-text-heading)" };
const _hoisted_3 = { class: "grid grid-cols-1 gap-4 sm:grid-cols-2" };
const _hoisted_4 = { class: "flex flex-col gap-2" };
import { Alert, MultipleSelect, SelectInput, TextInput } from "@eudonet/opal";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { buildRemoveChipLabel } from "@/shared/composables/removeChipLabel";
import { campaignFirstFiscalYearChoices, daysInMonth } from "../../../domain/entities/taxReceiptSettings";
import { TAX_RECEIPT_RECIPIENTS_PICKER_ENABLED, } from "../../../composables/useTaxReceiptNotificationRecipients";
import DonationSettingsFieldError from "./DonationSettingsFieldError.vue";
import DonationSettingsSection from "./DonationSettingsSection.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DonationSettingsTriggerTab',
    props: {
        campaignTriggerDay: {},
        campaignTriggerMonth: {},
        campaignFirstFiscalYear: {},
        recipients: {},
        recipientOptions: {},
        recipientsLoading: { type: Boolean },
        recipientsSearching: { type: Boolean },
        recipientsLoadError: { type: Boolean },
        triggerDateError: {},
        campaignFirstFiscalYearError: {},
        recipientsError: {}
    },
    emits: ["update:campaignTriggerDay", "update:campaignTriggerMonth", "update:campaignFirstFiscalYear", "update:recipients", "search"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t, locale } = useI18n();
        const removeChipLabel = buildRemoveChipLabel(t);
        const monthNumber = computed(() => Number(props.campaignTriggerMonth) || 1);
        const dayOptions = computed(() => Array.from({ length: daysInMonth(monthNumber.value) }, (_, i) => ({
            value: String(i + 1),
            label: String(i + 1),
        })));
        function capitalize(value) {
            return value.charAt(0).toLocaleUpperCase(locale.value) + value.slice(1);
        }
        const monthOptions = computed(() => {
            const format = new Intl.DateTimeFormat(locale.value, { month: "long" });
            return Array.from({ length: 12 }, (_, i) => ({
                value: String(i + 1),
                label: capitalize(format.format(new Date(2000, i, 1))),
            }));
        });
        // Only the current and the last closed fiscal years can be newly chosen: an older one would generate
        // and email every year since at once. A saved year that is no longer offered still shows, so the
        // select never renders an empty value.
        const firstFiscalYearOptions = computed(() => {
            const years = campaignFirstFiscalYearChoices(new Date().getFullYear()).map(String);
            if (props.campaignFirstFiscalYear !== "" && !years.includes(props.campaignFirstFiscalYear)) {
                years.push(props.campaignFirstFiscalYear);
                years.sort((a, b) => Number(b) - Number(a));
            }
            return [
                { value: "", label: t("finance.taxReceipts.settings.trigger.firstFiscalYearNone") },
                ...years.map((year) => ({ value: year, label: year })),
            ];
        });
        // Prose example, not a data date: the long month form reads better than the profile's numeric
        // format and carries no ambiguity between day and month.
        const fiscalYearExample = computed(() => {
            const year = new Date().getFullYear();
            // Same rule as OPDON: a 29 February trigger fires on 28 February in a non-leap year.
            const lastDayOfMonth = new Date(year, monthNumber.value, 0).getDate();
            const day = Math.min(Number(props.campaignTriggerDay) || 1, lastDayOfMonth);
            const date = new Intl.DateTimeFormat(locale.value, { day: "numeric", month: "long", year: "numeric" }).format(new Date(year, monthNumber.value - 1, day));
            return t("finance.taxReceipts.settings.trigger.fiscalYearExample", { date, fiscalYear: year - 1 });
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(DonationSettingsSection, {
                title: _ctx.$t('finance.taxReceipts.settings.trigger.title'),
                subtitle: _ctx.$t('finance.taxReceipts.settings.trigger.subtitleAutomatic'),
                "test-id": "donation-settings-trigger"
            }, {
                default: _withCtx(() => [
                    _createElementVNode("fieldset", _hoisted_1, [
                        _createElementVNode("legend", _hoisted_2, _toDisplayString(_ctx.$t("finance.taxReceipts.settings.trigger.dateLabel")), 1 /* TEXT */),
                        _createElementVNode("div", _hoisted_3, [
                            _createVNode(_unref(SelectInput), {
                                "model-value": __props.campaignTriggerDay,
                                options: dayOptions.value,
                                label: _ctx.$t('finance.taxReceipts.settings.trigger.dayLabel'),
                                "full-width": "",
                                "data-testid": "donation-settings-trigger-day",
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => (emit('update:campaignTriggerDay', $event)))
                            }, null, 8 /* PROPS */, ["model-value", "options", "label"]),
                            _createVNode(_unref(SelectInput), {
                                "model-value": __props.campaignTriggerMonth,
                                options: monthOptions.value,
                                label: _ctx.$t('finance.taxReceipts.settings.trigger.monthLabel'),
                                "full-width": "",
                                "data-testid": "donation-settings-trigger-month",
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (emit('update:campaignTriggerMonth', $event)))
                            }, null, 8 /* PROPS */, ["model-value", "options", "label"])
                        ]),
                        _createVNode(DonationSettingsFieldError, {
                            message: __props.triggerDateError ? _ctx.$t(__props.triggerDateError) : undefined,
                            "test-id": "donation-settings-trigger-date-error"
                        }, null, 8 /* PROPS */, ["message"])
                    ]),
                    _createVNode(_unref(TextInput), {
                        "model-value": _ctx.$t('finance.taxReceipts.settings.trigger.fiscalYearValue'),
                        label: _ctx.$t('finance.taxReceipts.settings.trigger.fiscalYearLabel'),
                        hint: fiscalYearExample.value,
                        readonly: "",
                        "full-width": "",
                        "data-testid": "donation-settings-trigger-fiscal-year"
                    }, null, 8 /* PROPS */, ["model-value", "label", "hint"]),
                    _createElementVNode("div", _hoisted_4, [
                        _createVNode(_unref(SelectInput), {
                            "model-value": __props.campaignFirstFiscalYear,
                            options: firstFiscalYearOptions.value,
                            label: _ctx.$t('finance.taxReceipts.settings.trigger.firstFiscalYearLabel'),
                            hint: _ctx.$t('finance.taxReceipts.settings.trigger.firstFiscalYearHint'),
                            "full-width": "",
                            "data-testid": "donation-settings-trigger-first-fiscal-year",
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => (emit('update:campaignFirstFiscalYear', $event)))
                        }, null, 8 /* PROPS */, ["model-value", "options", "label", "hint"]),
                        _createVNode(DonationSettingsFieldError, {
                            message: __props.campaignFirstFiscalYearError ? _ctx.$t(__props.campaignFirstFiscalYearError) : undefined,
                            "test-id": "donation-settings-trigger-first-fiscal-year-error"
                        }, null, 8 /* PROPS */, ["message"])
                    ]),
                    (_unref(TAX_RECEIPT_RECIPIENTS_PICKER_ENABLED))
                        ? (_openBlock(), _createBlock(_unref(MultipleSelect), {
                            key: 0,
                            "model-value": __props.recipients,
                            options: __props.recipientOptions,
                            label: _ctx.$t('finance.taxReceipts.settings.trigger.recipientsLabel'),
                            placeholder: _ctx.$t('finance.taxReceipts.settings.trigger.recipientsPlaceholder'),
                            "search-clear-label": _ctx.$t('common.clearSearch'),
                            "search-placeholder": _ctx.$t('finance.taxReceipts.settings.trigger.recipientsSearch'),
                            "confirm-label": _ctx.$t('finance.taxReceipts.settings.trigger.recipientsConfirm'),
                            "select-all-label": _ctx.$t('finance.taxReceipts.settings.trigger.recipientsSelectAll'),
                            "clear-all-label": _ctx.$t('finance.taxReceipts.settings.trigger.recipientsClearAll'),
                            "remove-chip-label": _unref(removeChipLabel),
                            "empty-label": _ctx.$t('finance.taxReceipts.settings.trigger.recipientsEmpty'),
                            "loading-label": _ctx.$t('finance.taxReceipts.settings.trigger.recipientsLoading'),
                            error: __props.recipientsError ? _ctx.$t(__props.recipientsError) : undefined,
                            hint: __props.recipientsLoadError ? _ctx.$t('finance.taxReceipts.settings.trigger.recipientsLoadError') : undefined,
                            disabled: __props.recipientsLoadError,
                            loading: __props.recipientsLoading || __props.recipientsSearching,
                            async: "",
                            "data-testid": "donation-settings-trigger-recipients",
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => (emit('update:recipients', $event))),
                            onSearch: _cache[4] || (_cache[4] = ($event) => (emit('search', $event)))
                        }, null, 8 /* PROPS */, ["model-value", "options", "label", "placeholder", "search-clear-label", "search-placeholder", "confirm-label", "select-all-label", "clear-all-label", "remove-chip-label", "empty-label", "loading-label", "error", "hint", "disabled", "loading"]))
                        : _createCommentVNode("v-if", true),
                    _createVNode(_unref(Alert), {
                        color: "info",
                        message: _ctx.$t('finance.taxReceipts.settings.trigger.systemRuleAutomatic'),
                        "data-testid": "donation-settings-trigger-system-rule"
                    }, null, 8 /* PROPS */, ["message"])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["title", "subtitle"]));
        };
    }
});
